import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Search, Grid3X3, List, Download, Trash2, ShoppingCart, FileText, MoreVertical, Palette, ShoppingBag } from "lucide-react";
import { useUserData } from "../../hooks/useUserData";

const Designs = () => {
  const { designs } = useUserData();
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const items = designs.filter((d) => `${d.name} ${d.productName}`.toLowerCase().includes(q.toLowerCase()));

  if (designs.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <h1 className="text-2xl font-bold text-zinc-900">Saved Designs</h1>
        <p className="text-sm text-zinc-500 mt-1">Re-use any design across new orders — we keep your print-ready files safely on hand.</p>
        <div className="py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-violet-50 text-violet-500 flex items-center justify-center mb-4">
            <Palette className="w-7 h-7" />
          </div>
          <div className="font-semibold text-zinc-900 text-lg">No saved designs yet</div>
          <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">Files you upload during checkout are saved here automatically. You can also upload a design any time and use it on a new order.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <button onClick={() => alert("File upload will work once the backend is live.")} className="inline-flex items-center gap-2 border border-zinc-200 text-zinc-800 px-5 py-2.5 rounded-lg text-xs font-bold hover:border-zinc-400 transition-colors">
              <Upload className="w-3.5 h-3.5" /> Upload a design
            </button>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 rounded-lg text-xs font-bold hover:bg-rose-500 transition-colors">
              <ShoppingBag className="w-3.5 h-3.5" /> Browse products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Saved Designs</h1>
            <p className="text-sm text-zinc-500 mt-1">Re-use any design across new orders — we keep your print-ready files safely on hand.</p>
          </div>
          <button onClick={() => alert("File upload will work once the backend is live.")} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 rounded-lg text-xs font-bold hover:bg-rose-500 transition-colors self-start">
            <Upload className="w-4 h-4" /> Upload a design
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by file name or product" className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-rose-400" />
          </div>
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            <button onClick={() => setView("grid")} className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${view === "grid" ? "bg-white text-rose-500 shadow" : "text-zinc-500"}`}><Grid3X3 className="w-4 h-4" /></button>
            <button onClick={() => setView("list")} className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${view === "list" ? "bg-white text-rose-500 shadow" : "text-zinc-500"}`}><List className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-zinc-200 py-16 text-center mt-5">
          <FileText className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <div className="font-semibold text-zinc-900">No designs match your search</div>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
          {items.map((d) => (
            <div key={d.id} className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:shadow-lg transition-all">
              <div className="aspect-square bg-zinc-50 overflow-hidden relative">
                <img src={d.thumb} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-white text-zinc-700 px-2 py-1 rounded">{d.type}</span>
              </div>
              <div className="p-4">
                <div className="font-semibold text-sm text-zinc-900 truncate">{d.name}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{d.productName} · {d.size}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">Updated {d.updated}</div>
                <div className="flex gap-2 mt-3">
                  <Link to={`/product/${d.productSlug}`} className="flex-1 bg-zinc-900 text-white text-xs font-bold py-2 rounded-md hover:bg-rose-500 transition-colors inline-flex items-center justify-center gap-1"><ShoppingCart className="w-3 h-3" /> Use</Link>
                  <button onClick={() => alert("Download will work once the backend is live.")} className="w-9 h-9 border border-zinc-200 rounded-md text-zinc-600 hover:text-rose-500 hover:border-rose-300 inline-flex items-center justify-center transition-colors"><Download className="w-3.5 h-3.5" /></button>
                  <button onClick={() => alert("Delete will work once the backend is live.")} className="w-9 h-9 border border-zinc-200 rounded-md text-zinc-600 hover:text-rose-500 hover:border-rose-300 inline-flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-zinc-200 mt-5 overflow-hidden">
          <ul className="divide-y divide-zinc-100">
            {items.map((d) => (
              <li key={d.id} className="px-5 py-4 flex items-center gap-4">
                <img src={d.thumb} alt={d.name} className="w-14 h-14 rounded-md object-cover bg-zinc-50 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-zinc-900 truncate">{d.name}</div>
                  <div className="text-xs text-zinc-500">{d.productName} · {d.type} · {d.size} · Updated {d.updated}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Link to={`/product/${d.productSlug}`} className="px-3 py-1.5 bg-zinc-900 text-white text-xs font-bold rounded-md hover:bg-rose-500 transition-colors">Use</Link>
                  <button onClick={() => alert("Download mocked")} className="w-8 h-8 text-zinc-500 hover:text-rose-500"><Download className="w-4 h-4" /></button>
                  <button onClick={() => alert("Delete mocked")} className="w-8 h-8 text-zinc-500 hover:text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  <button className="w-8 h-8 text-zinc-400"><MoreVertical className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Designs;
